import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { ConversationsListResponse } from "@shaarp/common";
import {
  type InfiniteData,
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";

const PAGE_SIZE = 30;

/**
 * Cache key of the team's conversation lists, every search included —
 * invalidate it after a change.
 */
export const conversationsListKey = (teamId: string | undefined) =>
  ["conversations", teamId] as const;

/**
 * Brings the cached list back to its first page. TanStack refetches every page
 * it holds, one after the other, each time the list is read again: the drawer
 * remounts its list on every opening and the chat invalidates it after each
 * answer, so a user who scrolled five pages paid five requests per opening,
 * for a list read from the top. Called when the drawer closes.
 */
export function keepFirstConversationsPage(
  queryClient: QueryClient,
  teamId: string | undefined
): void {
  queryClient.setQueriesData<
    InfiniteData<ConversationsListResponse, string | null>
  >({ queryKey: conversationsListKey(teamId) }, (data) =>
    data && data.pages.length > 1
      ? {
          pages: data.pages.slice(0, 1),
          pageParams: data.pageParams.slice(0, 1),
        }
      : data
  );
}

/**
 * The team's conversations, most recent first, a page at a time — what the
 * mobile sidebar lists under "Recent". `search` narrows it on the server, over
 * the conversations' titles and messages, as on the desktop. Goes through
 * `useApi()` like every read, with TanStack's infinite query for the cursor
 * pages.
 */
export function useConversationsList(search = "") {
  const api = useApi();
  const { teamSelected } = useTeams();

  return useInfiniteQuery({
    queryKey: [...conversationsListKey(teamSelected?.id), search],
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<ConversationsListResponse>(
        "/conversations",
        {
          params: {
            limit: PAGE_SIZE,
            ...(pageParam ? { cursor: pageParam } : {}),
            ...(search ? { search } : {}),
          },
        }
      );
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : null,
    enabled: !!teamSelected?.id,
    // A new search is a new key, so a cold one: the list on screen stays
    // until its results arrive, rather than blinking to skeletons on every
    // pause in the typing.
    placeholderData: keepPreviousData,
  });
}
